import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import { DataTable, FilterBar } from "@/components/data";
import type { DataTablePage, FilterSpec } from "@/components/data";
import type { FilterValues } from "@/lib/filters";
import { FreshnessLine } from "@/components/shell/FreshnessLine";
import { AlertCard } from "@/modules/protex/components/AlertCard";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import {
  FRICTION_DAYS,
  FRICTION_STAGES,
  frictionExportUrl,
  frictionGapsKey,
  frictionKey,
  getFriction,
  getFrictionGaps,
  messageFor,
  type FrictionGap,
  type FrictionGapStatus,
  type FrictionRow,
} from "./api";
import { Chip } from "./components/Chip";

// Labels only: every count, disposition and attribution is CVCE's (I-4).
const STAGE_LABEL: Record<string, string> = {
  cortex_research: "Cortex research",
  kb_entry: "KB entry",
  blueprint: "Blueprint",
  vox_draft: "Vox draft",
  visuals: "Visuals",
  review: "Review",
  publish: "Publish",
};

const SOURCE_LABEL: Record<string, string> = {
  note: "Reviewer note",
  finding: "Review finding",
  revise_round: "REVISE round",
  swing: "Verdict swing",
  render_reject: "Render reject",
  publish_reject: "Publish reject",
  bug: "Bug",
  gate_pushback: "GATE push-back",
};

const GAP_TONE: Record<FrictionGapStatus, string> = {
  filled: "border-ks-grey-4 text-ks-grey-1",
  explained: "border-ks-purple-3 text-ks-purple",
  open: "border-ks-warn text-ks-warn",
};

export const label = (map: Record<string, string>, key: string | null) =>
  key === null ? "Not set" : (map[key] ?? key.replace(/_/g, " "));

type Cell = { stage: string | null; cause: string | null };

export function FrictionTab({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const rawDays = Number(search.days);
  const days = (FRICTION_DAYS as readonly number[]).includes(rawDays) ? rawDays : 60;
  const [cell, setCell] = useState<Cell | null>(null);
  const [showExcluded, setShowExcluded] = useState(false);

  const query = useQuery({ queryKey: frictionKey(days), queryFn: () => getFriction(days) });
  const gapsQuery = useQuery({ queryKey: frictionGapsKey, queryFn: getFrictionGaps });

  const daysSpec: FilterSpec = {
    name: "days",
    label: "Window",
    kind: "choice",
    options: FRICTION_DAYS.map((d) => ({ value: String(d), label: `Last ${d} days` })),
  };
  const setDays = (next: FilterValues) => {
    setCell(null);
    void navigate({ to: ".", search: { ...search, days: Number(next.days) }, replace: true });
  };

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true">
        <p role="status" className="sr-only">
          Loading friction signals…
        </p>
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-ks-md bg-ks-grey-4/40" />
        ))}
      </div>
    );
  }

  if (query.isError || query.data?.data.available === false) {
    return (
      <AlertCard
        title="We couldn't load friction signals"
        detail={query.error ? messageFor(query.error, areaName) : "Try again shortly."}
      />
    );
  }

  const data = query.data!.data;
  const causes = [...new Set(data.matrix.map((c) => c.cause_class ?? "__none"))].sort();
  const stagesInMatrix = new Set(data.matrix.map((c) => c.stage));
  const stages: (string | null)[] = [...FRICTION_STAGES, ...(stagesInMatrix.has(null) ? [null] : [])];
  const cellN = (stage: string | null, cause: string) =>
    data.matrix.find((c) => c.stage === stage && (c.cause_class ?? "__none") === cause)?.n ?? 0;
  const stageTotal = (stage: string | null) =>
    data.matrix.filter((c) => c.stage === stage).reduce((sum, c) => sum + c.n, 0);

  let rows = data.rows.filter((r) => (showExcluded ? r.disposition === "excluded" : r.disposition !== "excluded"));
  if (cell) rows = rows.filter((r) => r.stage === cell.stage && (r.cause_class ?? "__none") === (cell.cause ?? "__none"));

  const page: DataTablePage = { pageIndex: 0, pageSize: rows.length || 1, rowCount: rows.length };
  const columns: ColumnDef<FrictionRow, unknown>[] = [
    { id: "when", header: "When", cell: ({ row }) => row.original.occurred_at.slice(0, 10) },
    { id: "source", header: "Source", cell: ({ row }) => label(SOURCE_LABEL, row.original.source) },
    { id: "stage", header: "Stage", cell: ({ row }) => label(STAGE_LABEL, row.original.stage) },
    {
      id: "cause",
      header: showExcluded ? "Excluded because" : "Cause",
      cell: ({ row }) =>
        showExcluded
          ? label({}, row.original.exclude_reason)
          : label({}, row.original.cause_class) + (row.original.data_gap_field ? ` · ${row.original.data_gap_field}` : ""),
    },
    { id: "subject", header: "Subject", cell: ({ row }) => row.original.subject_id ?? "–" },
    {
      id: "detail",
      header: "Detail",
      cell: ({ row }) => {
        const text = row.original.detail;
        return text.length > 90 ? `${text.slice(0, 90)}…` : text;
      },
    },
    { id: "by", header: "Attributed by", cell: ({ row }) => row.original.attribution ?? "–" },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <FilterBar
          layout="inline"
          filters={[daysSpec]}
          values={{ days: String(days) }}
          defaultValues={{ days: "60" }}
          onChange={setDays}
        />
        <div className="flex gap-2">
          {(["csv", "jsonl"] as const).map((format) => (
            <a
              key={format}
              href={frictionExportUrl(days, format)}
              data-nonmutating
              className="h-8 rounded-ks-sm border border-ks-purple-3 bg-ks-surface px-3 text-[13px] leading-8 text-ks-purple"
            >
              Export {format.toUpperCase()}
            </a>
          ))}
        </div>
      </div>
      <FreshnessLine meta={query.data!.meta} pending={query.isFetching} />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiCard label="Friction signals" value={data.kpis.total} />
        <KpiCard label="Attributed" value={data.kpis.attributed} />
        <KpiCard label="Excluded with reason" value={data.kpis.excluded} />
        <KpiCard label="Unattributed" value={data.kpis.unattributed} warn={data.kpis.unattributed > 0} />
      </div>

      <div className="flex flex-wrap gap-2 text-[12px] text-ks-grey-1">
        {Object.entries(data.kpis.by_source).map(([source, n]) => (
          <span key={source} className="rounded-ks-sm border border-ks-grey-4 px-2 py-0.5">
            {label(SOURCE_LABEL, source)}: {n}
          </span>
        ))}
      </div>

      {causes.length > 0 && (
        <div className="overflow-x-auto rounded-ks-md border border-ks-grey-4 bg-ks-surface p-3">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cause</TableHead>
                {stages.map((s) => (
                  <TableHead key={s ?? "none"}>
                    {label(STAGE_LABEL, s)}
                    <span className="block text-[11px] font-normal text-ks-grey-1">{stageTotal(s)}</span>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {causes.map((c) => (
                <TableRow key={c}>
                  <TableCell className="text-xs font-semibold text-ks-ink">{c === "__none" ? "Not set" : label({}, c)}</TableCell>
                  {stages.map((s) => {
                    const n = cellN(s, c);
                    return (
                      <TableCell key={s ?? "none"}>
                        {n > 0 ? (
                          <button
                            type="button"
                            data-nonmutating
                            data-matrix-cell={`${s ?? "none"}:${c}`}
                            onClick={() => {
                              setShowExcluded(false);
                              setCell({ stage: s, cause: c === "__none" ? null : c });
                            }}
                            className="h-7 w-10 rounded-ks-sm border border-ks-grey-4 bg-ks-grey-4/40 text-xs font-semibold text-ks-ink hover:bg-ks-purple-4"
                          >
                            {n}
                          </button>
                        ) : (
                          <span className="text-xs text-ks-grey-3">–</span>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <GapsPanel
        loading={gapsQuery.isLoading}
        gaps={gapsQuery.data?.data.available ? gapsQuery.data.data.gaps : null}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Chip
          label={`Excluded (${data.kpis.excluded})`}
          active={showExcluded}
          onClick={() => {
            setCell(null);
            setShowExcluded((v) => !v);
          }}
        />
        {cell && (
          <button
            type="button"
            data-nonmutating
            onClick={() => setCell(null)}
            className="w-fit rounded-ks-sm border border-ks-purple-3 bg-ks-surface px-3 py-1 text-[13px] text-ks-purple"
          >
            Clear cell filter ({label(STAGE_LABEL, cell.stage)} · {cell.cause ? label({}, cell.cause) : "Not set"})
          </button>
        )}
      </div>

      <DataTable columns={columns} rows={rows} page={page} onPageChange={() => {}} />
    </div>
  );
}

function GapsPanel({ loading, gaps }: { loading: boolean; gaps: FrictionGap[] | null }) {
  if (loading) return null;
  return (
    <div className="flex flex-col gap-2 rounded-ks-md border border-ks-grey-4 bg-ks-surface p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-ks-grey-1">Data gaps</p>
      {gaps === null ? (
        <p className="text-sm text-ks-grey-1">The gaps list could not be loaded.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {gaps.map((g) => (
            <li key={g.gap_key} data-gap={g.gap_key} className="flex gap-2 text-sm text-ks-ink">
              <span className={`h-fit shrink-0 rounded-ks-sm border px-2 py-0.5 text-[11px] font-semibold ${GAP_TONE[g.status]}`}>
                {g.status}
              </span>
              <span>
                <span className="font-semibold">{label({}, g.gap_key)}</span>
                {g.rows_affected != null ? <span className="text-ks-grey-1"> · {g.rows_affected} rows</span> : null}
                <span className="block text-[13px] text-ks-grey-1">{g.why}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function KpiCard({ label: text, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <div className="flex flex-col gap-1 rounded-ks-md border border-ks-grey-4 bg-ks-surface p-3">
      <span className="text-xs font-semibold uppercase tracking-wide text-ks-grey-1">{text}</span>
      <span className={`text-2xl font-bold ${warn ? "text-ks-warn" : "text-ks-ink"}`}>{value}</span>
    </div>
  );
}
